import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';

import type { PopulationTab } from '../../helpers/custom-population';

import Icons from 'assets/icons';

const DRAG_ACTIVATION_DISTANCE_PX = 5;
const DND_MODIFIERS = [restrictToHorizontalAxis, restrictToParentElement];

interface SortableTabProps {
  tab: PopulationTab;
  isActive: boolean;
  readOnly?: boolean;
  onSelect: (key: string) => void;
  onEdit: (key: string) => void;
}

const SortableTab = ({
  tab,
  isActive,
  readOnly,
  onSelect,
  onEdit,
}: SortableTabProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.key, disabled: readOnly });

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={[
        {
          maxWidth: '220px',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: '0.375rem',
          borderWidth: '1px',
          px: 1,
          py: 0.5,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: 600,
        },
        isActive
          ? {
              borderColor: 'tokens.navy.200',
              bgcolor: 'tokens.navy.100',
              color: 'tokens.navy.900',
            }
          : {
              borderColor: 'tokens.gray.300',
              bgcolor: 'common.white',
              color: 'tokens.navy.800',
            },
        isDragging && { zIndex: 10, opacity: 0.8, boxShadow: 8 },
      ]}
      data-testid={`admit-population-tab-${tab.key}`}
    >
      {!readOnly && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="flex cursor-grab text-gray-500"
          aria-label={`Reorder ${tab.name}`}
          data-testid={`admit-population-drag-${tab.key}`}
          {...attributes}
          {...listeners}
        >
          <Icons.Grip className="size-4" aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className="min-w-0 truncate"
        aria-pressed={isActive}
        title={tab.name}
        onClick={() => onSelect(tab.key)}
      >
        {tab.name}
      </button>
      {tab.kind === 'custom' && !readOnly && (
        <IconButton
          variant="reserve-text"
          size="baby"
          icon={<Icons.EditPencil aria-hidden="true" />}
          aria-label={`Edit ${tab.name}`}
          className="size-3 hover:!bg-transparent"
          onClick={() => onEdit(tab.key)}
          data-testid={`admit-population-edit-${tab.key}`}
        />
      )}
    </Stack>
  );
};

interface PopulationTabsProps {
  tabs: PopulationTab[];
  activeTabKey: string | null;
  readOnly?: boolean;
  onSelect: (key: string) => void;
  onEdit: (key: string) => void;
  onAdd: () => void;
  onReorder: (activeKey: string, overKey: string) => void;
}

const PopulationTabs = ({
  tabs,
  activeTabKey,
  readOnly,
  onSelect,
  onEdit,
  onAdd,
  onReorder,
}: PopulationTabsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={DND_MODIFIERS}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={tabs.map((tab) => tab.key)}
        strategy={horizontalListSortingStrategy}
      >
        <Stack
          direction="row"
          role="group"
          aria-label="Populations"
          data-testid="admit-population-tabs"
          sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
        >
          {tabs.map((tab) => (
            <SortableTab
              key={tab.key}
              tab={tab}
              isActive={tab.key === activeTabKey}
              readOnly={readOnly}
              onSelect={onSelect}
              onEdit={onEdit}
            />
          ))}
          {!readOnly && (
            <Button
              type="button"
              variant="reserve-text"
              size="xs"
              iconBefore={<Icons.Plus aria-hidden="true" />}
              onClick={onAdd}
              data-testid="admit-population-add"
            >
              New
            </Button>
          )}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};

export default PopulationTabs;
